import { useState } from 'react'
import { Link } from 'react-router'
import type { JSX, SyntheticEvent } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useBrand } from '@/brand/context'
import { Button } from '@/ui/Button'
import { TextField } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { PublicPage } from './PublicPage'

/**
 * `/forgot-password` — asks for a reset link.
 *
 * The screen deliberately says the **same thing whether or not the address has
 * an account**, because the API deliberately does (`ForgotPasswordResponse`:
 * "says nothing about whether the address exists"). A page that said "no such
 * account" would undo that in one line and turn the form into an oracle for
 * enumerating customers.
 *
 * Rate limiting lives on the server, and its refusal is `RATE_LIMITED` through
 * the ordinary envelope — so the inline error shows it without this page
 * knowing the policy.
 */

/**
 * Mirrors `PASSWORD_RESET_TTL_HOURS` in `app/security/verification.py`, where
 * the reason for the number is written down: a reset link *is* control of the
 * account, so it gets an hour where an email-verification link gets a day.
 *
 * Stated rather than left vague (AUTH-15). "It works once and expires" invites
 * the reader to guess, and the guess that costs them is the generous one —
 * somebody who assumes a day comes back tomorrow to a dead link and starts
 * again, which is one more mail and one more chance to give up.
 */
const RESET_LINK_LIFETIME = 'one hour'

export function ForgotPassword(): JSX.Element {
  const brand = useBrand()
  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)

  const request = useMutation((body: { email: string }) =>
    call(() => api.POST('/api/v1/auth/password/forgot', { body })),
  )

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const address = email.trim().toLowerCase()
    const result = await request.run({ email: address })
    if (result.ok) setSentTo(address)
  }

  /*
    AUTH-57. The confirmation is **this screen**, not a different one.

    Screen 08 draws one screen and annotates the after-submit state on it
    ("If an account exists for that address, a reset link is on its way." —
    shown every time, regardless): the title, the subtitle and the footer link
    all stay put, and the form is replaced by the sentence. The app had built a
    second page with its own `h1` ("Check your email"), its own two paragraphs
    and its own footer, so submitting a one-field form navigated somewhere.

    Keeping the frame is also what makes the answer read as the answer to what
    was just asked, rather than as a page that might have arrived for some
    other reason — which matters here more than usual, because the sentence is
    deliberately the same whether or not the address has an account.
  */
  const footer = (
    <>
      {/* AUTH-58. One link, and it goes back to sign-in. Before the submit the
          app asked "Remembered it?", which is a question for somebody who
          arrived by mistake; after it, the app offered "Wrong address? Try
          another one", which invites a second guess at a form whose whole
          design is that it never confirms a guess. The design's single "Back
          to log in" is the honest offer in both states, under the app's own
          spelling of it: *Sign in* is the product's word, decided by the owner
          on 2026-09-06 and recorded at `docs/MOCKUP-GAP-AUDIT.md` V5(b). */}
      <Link className="ar-link" to="/login">
        Back to sign in
      </Link>
    </>
  )

  if (sentTo) {
    return (
      <PublicPage
        title="Reset your password"
        subtitle={`We'll email you a link. It expires in ${RESET_LINK_LIFETIME}.`}
        footer={footer}
      >
        <div className="ar-card__body ar-stack">
          {/* The lifetime is not repeated here: AUTH-57 keeps the frame, so
              the subtitle above still says it, and printing it twice on one
              screen reads as two different clocks. */}
          <p style={{ margin: 0 }}>
            If <strong>{sentTo}</strong> has an {brand.productName} account, a reset link is on its
            way. It works once.
          </p>
          <p className="ar-muted" style={{ margin: 0 }}>
            Nothing after a few minutes? Check spam, and confirm you typed the address you signed up
            with.
          </p>
          <Button
            block
            onClick={() => {
              setSentTo(null)
              request.reset()
            }}
          >
            Use a different address
          </Button>
        </div>
      </PublicPage>
    )
  }

  return (
    <PublicPage
      title="Reset your password"
      /* AUTH-33. Screen 08's `.sc-auth__head p`. The lifetime was stated only
         after the submit, which is the wrong side of it: somebody deciding
         whether to ask for a link wants to know it is short-lived *before*
         they go and open their mail an hour later. */
      subtitle={`We'll email you a link. It expires in ${RESET_LINK_LIFETIME}.`}
      footer={footer}
    >
      {/* `method="post"` so a submit with dead JavaScript cannot put this email
          address in a URL. Not a credential, but it is personal data under DPDP and
          the query string is logged. Reasoning in full on `Login.tsx`'s form. */}
      <form
        method="post"
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className="ar-card__body ar-stack">
          <InlineError failure={request.state.failure} />
          {/* AUTH-56. "Email", a placeholder, and no help. The help said "the
              address you signed up with", which is the same thing the label and
              the screen's title already say — and the placeholder the design
              gives it does more work, because it shows the shape of what goes
              in the box. */}
          <TextField
            label="Email"
            type="email"
            required
            autoFocus
            autoComplete="email"
            placeholder="you@company.in"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              request.reset()
            }}
          />
          {/* AUTH-34 / AUTH-35. In the body, and at `lg`/`block` — screens 08
              and 09 are the two where the app had left the CTA at its default
              size and intrinsic width, right-aligned in a footer, so the one
              action on a one-field screen was also the smallest thing on it. */}
          <Button
            tone="primary"
            size="lg"
            block
            type="submit"
            busy={request.pending}
            busyLabel="Sending"
          >
            Send reset link
          </Button>
        </div>
      </form>
    </PublicPage>
  )
}
